//EXTERNAL
import { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { useHistory, useLocation, useParams } from "react-router-dom";
//COMPONENTS
import * as ComponentLibrary from "@picnicmedia/masanatemples123";
//FEATURES
import { ADFORM_REPORTING_CREATIVE_DATA, ADFORM_REPORTING_MODAL_PRESET_DATA, AdformReporting, openReportingModal } from "../features/adform/reporting";
import { BlurryImage, DEFAULT_SORT, DEFAULT_TABLE_STATE, DeleteCreativeModal, FILTER_OPTIONS, FORMATS, FilterButton, MEDIA_TYPES, PAGE_LIMIT, PackageUploadModal, PageHeader, SearchBar, Table, TableSlider, useColumnsData, useCreativeActions, useMediaTypes } from "../features/creative/library";
import { CookieConsent } from "../features/notifications/cookie-consent";
// LIBS
import { s3_signed_url } from "../libs/amplify";
import { logger } from "../libs/logger";
import { Google_Analytics_Event } from "../libs/google-analytics";
//CONTEXT
import { useUserState } from "../contexts/user.context";
//QUERIES
import { LIST_CREATIVES_FILTERS } from "../graphql/queries";
//CONFIG
import config from "../configs/config";
//TYPES
import { AdvertisersDataProps, CreativesTypes, ParamTypes, DataProps, CreativeDataProps, AdformReportingModalPresetDataProps } from "../features/creative/library/types";
import { AdformReportingCreativeData } from "../features/adform/reporting/types";
import { User } from "../types/user.context";

export default function Library() {
  const user: User = useUserState();
  const teamId = user.teamId;
  /**
   * JA (05-09-23)
   * Helper functions for interacting with the url params.
   */
  const history = useHistory();
  const search = useLocation().search;
  const params = useParams<ParamTypes>();
  const qs = new URLSearchParams(search);
  const getQs = (name: string) => qs.get(name);
  const hasQs = (name: string) => qs.has(name);
  const setQs = (name: string, val: string) => qs.set(name, val);
  const deleteQs = (name: string) => qs.delete(name);
  const updateQs = () => history.push({ search: `?${qs}` });

  const [creatives, setCreatives] = useState<CreativesTypes[]>([]);
  const [totalCreativesCount, setCreativesTotalCount] = useState(0);
  const [creativeItemsCount, setCreativeItemsCount] = useState(0);
  const [advertiserName, setAdvertiserName] = useState("");
  const [advertisersData, setAdvertisersData] = useState<AdvertisersDataProps[]>([]);
  const [advertiserId, setAdvertiserId] = useState("");
  const [advertiserNamesCount, setAdvertiserNamesCount] = useState(0);
  const [advertiserAdsTotalCount, setAdvertiserAdsTotalCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFormat, setSelectedFormat] = useState(FORMATS);
  const [selectedMedia, setSelectedMedia] = useState(MEDIA_TYPES);
  const [tableType, setTableType] = useState(DEFAULT_TABLE_STATE);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [currentPageNumber, setCurrentPageNumber] = useState("1");
  const [clearInput, setClearInput] = useState(false);
  const [deleteCreativeModal, setDeleteCreativeModal] = useState({ open: false, creative: "" });
  const [packageModalOpen, setPackageModalOpen] = useState(false);
  const [adformReportingModalOpen, setAdformReportingModalOpen] = useState(false);
  const [adformReportingModalPresetData, setAdformReportingModalPresetData] = useState<AdformReportingModalPresetDataProps>(ADFORM_REPORTING_MODAL_PRESET_DATA);
  const [adformReportingCreativeData, setAdformReportingCreativeData] = useState<AdformReportingCreativeData>(ADFORM_REPORTING_CREATIVE_DATA);

  /**
   * JA (05-09-23)
   * Flag for checking current table type.
   * Flag for checking if brand name param exists in url.
   */
  const isAdsTable = tableType === "Ads";
  const isBrandsTable = tableType === "Brands";
  const isBrandParamExist = hasQs("brand")!;
  /**
   * JA (05-09-23)
   * Based on current table type, assign the query string to be added (0-index) and to be removed (1-index)
   */
  const [addQsSearch, deleteQsSearch] = isAdsTable ? ["adName", "brand"] : ["brand", "adName"];
  /**
   * JA (05-09-23)
   * Based on current table type, assign the count number for total amount of items.
   */
  const itemTotalCount = isAdsTable || advertiserId ? totalCreativesCount : advertiserAdsTotalCount;

  const queryVariables = {
    teamId,
    advertiserId,
    offset,
    limit: PAGE_LIMIT,
    filters: {
      adName: (isAdsTable && searchQuery) || "",
      advertiserName: (isBrandsTable && searchQuery) || "",
      format: selectedFormat?.split(","),
      media: selectedMedia?.split(",").flatMap(useMediaTypes),
    },
    sorts: sort,
  };

  /**
   * JH (29-07-26)
   * Pushes a creatives list response into component state. Shared by the initial query and by
   * refreshCreatives below, so both routes update the same state the table renders from.
   */
  const applyCreativeData = async (creativeData: DataProps) => {
    const {
      readAllCreativesAdvertiserFilters: { totalCount, data, advertisersData, totalAdvertiserAdsCount },
    } = creativeData;

    setCreatives(await getPreviews(data));
    setCreativesTotalCount(totalCount);
    setCreativeItemsCount(data.length);

    setAdvertiserName(params.advertiserName);
    setAdvertisersData(advertisersData);
    setAdvertiserNamesCount(advertisersData.length);
    setAdvertiserAdsTotalCount(totalAdvertiserAdsCount);
  };

  const { loading, refetch } = useQuery(LIST_CREATIVES_FILTERS, {
    variables: queryVariables,
    fetchPolicy: "network-only",
    onCompleted: applyCreativeData,
    onError: (error) => {
      logger({ error, user, context: { origin: "Library_LIST_CREATIVES_FILES" } });
      Google_Analytics_Event({ name: "error", category: error.message + " " + error.cause, action: "Library_LIST_CREATIVES_FILES", label: user.email });
    },
  });

  /**
   * JH (29-07-26)
   * Re-runs the creatives query and applies the result. Handed to the creative actions so duplicating
   * or deleting refreshes the table in place — refetch alone does not re-fire onCompleted.
   *
   * The creatives list is read with a DynamoDB scan (eventually consistent), so a row written moments
   * ago (e.g. a just-ingested package) may be absent from the first refetch. When the caller passes the
   * id it's expecting, retry a few times until the scan catches up rather than showing a stale table.
   */
  const refreshCreatives = async (expectId?: string) => {
    for (let attempt = 0; attempt < 4; attempt++) {
      const { data } = await refetch();
      if (data) {
        await applyCreativeData(data);
        const items: CreativeDataProps[] = data.readAllCreativesAdvertiserFilters?.data || [];
        if (!expectId || items.some((c) => c.id === expectId)) return;
      }
      await new Promise((resolve) => setTimeout(resolve, 1200));
    }
  };

  /**
   * JH (29-07-26)
   * Live thumbnail for an uploaded package: a scaled-down iframe of the unpacked index.html. Scales to
   * a 60px-wide cell using the package's own dimensions, and disables pointer events so clicks fall
   * through to the row handler (which opens the full preview).
   */
  const packagePreviewThumbnail = (creative: CreativeDataProps) => {
    const width = creative.metadata?.dimensions?.width || 320;
    const height = creative.metadata?.dimensions?.height || 480;
    const scale = 60 / width;
    const indexUrl = `https://${config.s3.BUCKET}.s3.${config.s3.REGION}.amazonaws.com/public/${user.teamId}/${creative.id}/index.html`;
    return (
      <div style={{ width: "60px", height: `${Math.round(height * scale)}px`, borderRadius: "10px", overflow: "hidden", border: "1px solid #eee", background: "#fff" }}>
        <iframe title={`preview-${creative.id}`} src={indexUrl} width={width} height={height} scrolling={"no"} loading={"lazy"} style={{ border: 0, transform: `scale(${scale})`, transformOrigin: "top left", pointerEvents: "none" }} />
      </div>
    );
  };

  const getPreviews = async (data: CreativeDataProps[]) => {
    const Image = BlurryImage();
    return Promise.all(
      data.map(async (creative: CreativeDataProps) => ({
        ...creative,
        // Packages have no format-preview-original.png screenshot (Studio never renders them in its
        // editor), so instead of a static tile we show a live, scaled-down iframe of the unpacked
        // index.html — the actual rendered format. pointer-events are disabled so the row click still
        // opens the full preview. Templates keep their generated screenshot thumbnail. JH
        previewThumbnail: creative.metadata?.kind === "package" ? packagePreviewThumbnail(creative) : <Image src={`${await s3_signed_url({ teamId: user.teamId, creativeId: creative.id, fileName: "format-preview-original.png" })}`} />,
      })),
    );
  };

  /**
   * JA (05-09-23)
   * Navigate user to advertiser speciifc page.
   */
  const handleSpecificAdvertiserLink = (advertiserId: string, advertiserName: string) => {
    setAdvertiserId(advertiserId);
    setAdvertiserName(advertiserName);
  };

  /**
   * JA (05-09-23)
   * Helper function for updating query strings in url.
   */
  const changeParams = (inputValue: string | string[], paramType: string) => {
    setQs(paramType, Array.isArray(inputValue) ? inputValue.join(",") : inputValue);
    updateQs();
  };

  /**
   * JA (05-09-23)
   * Controls functionality for deleting a creative.
   */
  const { handleDeleteCreative } = useCreativeActions({
    getPreviews,
    deleteCreativeModal,
    setDeleteCreativeModal,
    advertiserId,
    queryVariables,
    refreshCreatives,
  });

  /**
   * JA (05-09-23)
   * Controls the adform reporting modal.
   */
  const handleOpenReportingModal = openReportingModal({ advertiserId, advertiserName, setAdformReportingModalPresetData, setAdformReportingModalOpen, setAdformReportingCreativeData });

  /**
   * JA (05-09-23)
   * adsColumns and brandsColumns are the populated data to be consumed by their respective table types.
   */
  const { adsColumns, brandsColumns } = useColumnsData({
    getPreviews,
    handleSpecificAdvertiserLink,
    deleteCreativeModal,
    setDeleteCreativeModal,
    advertiserId,
    advertiserName,
    requestTableData: tableType,
    queryVariables,
    handleOpenReportingModal,
    sort,
    refreshCreatives,
  });

  useEffect(() => {
    setQs("sort", getQs("sort")! || "desc");
    setQs("page", getQs("page")! || "1");
    setQs(addQsSearch, getQs(addQsSearch) || "");
    /**
     * JA (05-09-23)
     * When user switches tables, if adName param is present, remove it and replace with brand param. Vice versa.
     * The search input previously present, clear it and default to first page.
     * Default to the first page.
     */
    if (hasQs(deleteQsSearch)) {
      deleteQs(deleteQsSearch);
      setClearInput(true);
      setQs("page", "1");
    }
    updateQs();
  }, [location, tableType]);

  /**
   * JA (05-09-23)
   * On navigation to advertiser specific page, clear input and set table type to ads.
   */
  useEffect(() => {
    setClearInput(true);
    setTableType("Ads");
  }, [params.advertiserId]);

  /**
   * JA (05-09-23)
   * If page url param is not found, default to first page.
   */
  useEffect(() => {
    if (getQs(addQsSearch) !== "") {
      setQs("page", "1");
      updateQs();
    }
  }, [searchQuery]);

  /**
   * JA (05-09-23)
   * If brand url param is present, slider component to default to `Brands` option state.
   */
  useEffect(() => {
    setTableType(isBrandParamExist ? "Brands" : "Ads");
  }, []);

  /**
   * JA (05-09-23)
   * Disable native browser control interaction on library page.
   * This is until we have a cleaner interface for managing and syncing url and app state for the library page.
   */
  useEffect(() => {
    return history.listen(() => history.action === "POP" && history.go(1));
  }, [history]);

  /**
   * JA (05-09-23)
   * For links to be shareable, use url params in link to update state of page on initial load.
   */
  useEffect(() => {
    setSearchQuery(getQs(addQsSearch)!);
    setSelectedFormat(getQs("format")!);
    setSelectedMedia(getQs("media")!);
    setCurrentPageNumber(getQs("page")!);
    setAdvertiserId(params.advertiserId);
    setSort({ ...DEFAULT_SORT, [getQs("sort")! || "desc"]: true });
    setOffset(Math.max(0, (+getQs("page")! - 1) * PAGE_LIMIT));
  }, [search]);

  return (
    <>
      <div className="picnic-font flex flex-column items-center min-vh-100 bg-near-white">
        <div className={"w-100"}>
          <ComponentLibrary.PCSpacer type={"padding"} position={"around"} size={5}>
            <ComponentLibrary.PCContainerMain color={"bg-transparent"}>
              <div className={"ph4 pv2 bg-white br4 shadow-light"} style={{ maxHeight: "220px" }}>
                <PageHeader advertiserIdParams={params.advertiserId} advertiserName={advertiserName} user={user} handleOpenReportingModal={handleOpenReportingModal} onUploadPackageClick={() => setPackageModalOpen(true)} />
                <div className="relative flex h-100">
                  <SearchBar adAndBrandNames={getQs(addQsSearch)!} paramsSetter={addQsSearch} changeParams={changeParams} clearInput={clearInput} setClearInput={setClearInput} requestTableData={tableType} />
                  <div className="mt5 mb3 flex">
                    {/* Remove slider on advertiser specific pages */}
                    {!params.advertiserId && (
                      <>
                        <TableSlider setRequestTableData={setTableType} brand={hasQs("brand")} urlParams={qs} />
                        <ComponentLibrary.PCSpacer type={"margin"} position={"right"} size={3} />
                      </>
                    )}
                    {/* Show filter button on ads table */}
                    {(addQsSearch === "adName" || params.advertiserId) && <FilterButton options={FILTER_OPTIONS} changeParams={changeParams} adName={hasQs("adName")} brand={hasQs("brand")} matchedOptsSelections={[getQs("format")!, getQs("media")!]} urlParams={qs} />}
                  </div>
                </div>
              </div>
              <Table columns={[adsColumns, brandsColumns]} data={[creatives, advertisersData]} itemsCount={[creativeItemsCount, advertiserNamesCount]} requestTableData={tableType} advertiserId={advertiserId} loading={loading} paginatedTotal={itemTotalCount} offset={offset} limit={PAGE_LIMIT} currentPageNumber={currentPageNumber} setOffset={setOffset} updateSortOrder={(sort) => setSort(sort)} sortOrder={sort} updateParams={changeParams} />
            </ComponentLibrary.PCContainerMain>
          </ComponentLibrary.PCSpacer>
        </div>
      </div>
      <DeleteCreativeModal handleDeleteCreative={handleDeleteCreative} deleteCreativeModal={deleteCreativeModal} setDeleteCreativeModal={setDeleteCreativeModal} />
      <PackageUploadModal
        open={packageModalOpen}
        setOpen={setPackageModalOpen}
        user={user}
        advertisersData={advertisersData}
        presetAdvertiser={params.advertiserId ? { advertiserId: params.advertiserId, advertiserName } : undefined}
        refreshCreatives={refreshCreatives}
      />
      <AdformReporting advertiserId={advertiserId} advertiserName={advertiserName} adformReportingModalOpen={adformReportingModalOpen} adformReportingCreativeData={adformReportingCreativeData} adformReportingModalPresetData={adformReportingModalPresetData} setAdformReportingModalOpen={setAdformReportingModalOpen} setAdformReportingModalPresetData={setAdformReportingModalPresetData} setAdformReportingCreativeData={setAdformReportingCreativeData} />
      <CookieConsent />
    </>
  );
}
